import { collapseConsecutiveMessages } from './mergeTranscriptTurn';

export interface InterviewChatMessage {
  id: string;
  sender: 'ai' | 'user';
  text: string;
  timestamp: string;
}

export interface ConversationHistoryEntry {
  role?: string;
  content?: string;
  question?: string;
  response?: string;
  timestamp?: string;
  question_number?: number;
}

function formatTimestamp(value?: string): string {
  if (!value) {
    return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function roleToSender(role?: string): 'ai' | 'user' | null {
  const normalized = (role || '').trim().toLowerCase();
  if (normalized === 'assistant' || normalized === 'agent' || normalized === 'ai') {
    return 'ai';
  }
  if (normalized === 'user') {
    return 'user';
  }
  return null;
}

function isLegacyQaEntry(entry: ConversationHistoryEntry): boolean {
  return Boolean(entry.question || entry.response);
}

/**
 * Normalize legacy Q&A pairs or voice-agent role/content turns into chat messages
 * for FeedbackDashboard.
 */
export function conversationHistoryToMessages(
  history: ConversationHistoryEntry[] | null | undefined,
  defaultTimestamp?: string
): InterviewChatMessage[] {
  if (!history || history.length === 0) {
    return [];
  }

  const messages: InterviewChatMessage[] = [];

  history.forEach((entry, index) => {
    const baseTs = formatTimestamp(entry.timestamp || defaultTimestamp);

    if (isLegacyQaEntry(entry)) {
      const questionNumber = entry.question_number ?? index + 1;
      if (entry.question?.trim()) {
        messages.push({
          id: `q-${questionNumber}`,
          sender: 'ai',
          text: entry.question.trim(),
          timestamp: baseTs,
        });
      }
      if (entry.response?.trim()) {
        messages.push({
          id: `a-${questionNumber}`,
          sender: 'user',
          text: entry.response.trim(),
          timestamp: baseTs,
        });
      }
      return;
    }

    const sender = roleToSender(entry.role);
    const text = entry.content?.trim();
    if (!sender || !text) {
      return;
    }

    messages.push({
      id: `turn-${index + 1}`,
      sender,
      text,
      timestamp: baseTs,
    });
  });

  return collapseConsecutiveMessages(messages);
}
